import { getApi, postApi } from 'utils/Api';
import { prepareUrlParams } from 'utils/Urls';

export type BankAccountVerificationStatus =
  | 'mismatch'
  | 'failed'
  | 'created'
  | 'verified'
  | 'invalid'
  | null; //null for existing bank accounts
export type BankAccountApprovalStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'auto_approved'
  | null; //null for existing bank accounts

export type BankAccountResponse = {
  id: number | string;
  account_number: string;
  beneficiary_name: string | null;
  ifsc: string;
  approval_status: BankAccountApprovalStatus;
  verification_status: BankAccountVerificationStatus;
  is_active: boolean;
  created_at: string | null; // '2021-02-01 08:03:39'
  deactivated_at: string | null; // '2021-01-01 08:03:39'
};

export type FetchBankAccountsSchemaContract = {
  data: {
    count: number;
    items: BankAccountResponse[];
    meta?: {
      is_penny_testing_limit_exceeded?: boolean;
      employee_name?: string;
    };
  };
};

const FetchBankAccountsSchema = {
  type: 'object',
  properties: {
    data: {
      type: 'object',
      properties: {
        count: { type: 'number' },
        items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              id: { type: ['number', 'string'] },
              account_number: { type: 'string' },
              ifsc: { type: 'string' },
              beneficiary_name: { type: ['string', 'null'] },
              approval_status: {
                type: ['string', 'null'],
                enum: ['pending', 'approved', 'rejected', 'auto_approved', null],
              },
              verification_status: {
                type: ['string', 'null'],
                enum: ['mismatch', 'failed', 'created', 'verified', 'invalid', null],
              },
              is_active: { type: 'boolean' },
              created_at: { type: ['string', 'null'] },
              deactivated_at: { type: ['string', 'null'] },
            },
          },
        },
        meta: {
          type: 'object',
          properties: {
            is_penny_testing_limit_exceeded: { type: 'boolean' },
            employee_name: { type: 'string' },
          },
        },
      },
      required: ['count', 'items'],
    },
  },
  required: ['data'],
};

export type BankAccountRequest = {
  id: number | string;
  employee_name: string;
  beneficiary_name: string;
  account_number: string;
  ifsc: string;
  verification_status: NonNullable<BankAccountVerificationStatus>;
  approval_status: NonNullable<BankAccountApprovalStatus>;
  created_at: number;
  deactivated_at: number | null;
};

export type BankAccountRequestDetails = {
  id: number;
  employee_name: string;
  employee_type: 'employee' | 'contractor';
  mobile: string;
  email: string;
  beneficiary_name: string;
  account_number: string;
  ifsc: string;
  verification_status: NonNullable<BankAccountVerificationStatus>;
  approval_status: NonNullable<BankAccountApprovalStatus>;
  change_staging_id: number;
  attachment_type: 'cheque' | 'bank_statement';
  attachment: string; //s3 url
  created_at: number;
};

export type GetBankAccountRequestsResponse = {
  data: {
    count: number;
    has_more: boolean;
    total_count: number;
    items: BankAccountRequest[];
  };
};

export type AddAccountOtpResponseContract = {
  message: string;
  status: string;
};

export type VerifyAccountResponseContract = {
  id: number | string;
  beneficiary_name: string;
  account_number: string;
  ifsc: string;
  verification_status: NonNullable<BankAccountVerificationStatus>;
  approval_status: BankAccountApprovalStatus; // null if verification fails or verification status is mismatch
};

export type GetBankAccountRequestsQueryParams = {
  count: number;
  skip: number;
  approval_statuses?: BankAccountApprovalStatus[];
  verification_statuses?: BankAccountVerificationStatus[];
};

export type VerifyAccountPayload = {
  two_factor_auth_otp: string;
  account_number: string;
  ifsc: string;
  user_id?: number;
  beneficiary_name?: string;
  file_id?: string;
  file_type?: string;
  payment_mode?: string;
  is_retry_with_proof?: boolean;
};

export type ApproveRejectPayload = {
  reason: string;
  change_request_ids: string[];
};

export type ApproveRejectResponse = {
  data: {
    total: number;
    successful: number;
    failed: number;
    failed_request_ids: string[];
  };
};

export const FETCH_BANK_ACCOUNTS = 'FETCH_BANK_ACCOUNTS';
export const ADD_BANK_ACCOUNT = 'ADD_BANK_ACCOUNT';
export const BANK_PROOF_UPLOAD = 'BANK_PROOF_UPLOAD';
export const SEND_OTP = 'SEND_OTP';
export const VERIFY_ACCOUNT = 'VERIFY_ACCOUNT';
export const GET_BANK_ACCOUNT_REQUESTS = 'GET_BANK_ACCOUNT_REQUESTS';
export const SEND_FOR_APPROVAL = 'SEND_FOR_APPROVAL';

const employeeBankAccount = {
  fetchBankAccounts: (queryParams: { user_id?: number; limit: number; }) => {
    const queryParamsString = new URLSearchParams(
      queryParams as unknown as Record<string, string>,
    ).toString();
    const url = '/user/bank-account/details?' + queryParamsString;
    return getApi<FetchBankAccountsSchemaContract>({
      url,
      responseSchema: FetchBankAccountsSchema,
    });
  },

  addBankProof: ({
    file,
    proofType,
  }: {
    file: File;
    proofType: BankAccountRequestDetails['attachment_type'];
  }) => {
    const formData = new FormData();
    formData.append('file_name', file.name);
    formData.append('file', file);
    formData.append('proof_type', proofType);
    return postApi<
      {
        file_id: string;
        signed_url: string;
      },
      FormData
    >(
      {
        url: `/user/bank-account/upload-document`,
        responseSchema: {},
        requestData: formData,
      },
      'multipart',
    );
  },

  sendOtp: (payload: Omit<VerifyAccountPayload, 'two_factor_auth_otp'>) => {
    return postApi<AddAccountOtpResponseContract, Omit<VerifyAccountPayload, 'two_factor_auth_otp'>>({
      url: `/user/bank-account/send-otp`,
      requestData: payload,
      responseSchema: {},
    });
  },

  verifyAccount: (payload: VerifyAccountPayload) => {
    return postApi<VerifyAccountResponseContract, VerifyAccountPayload>({
      url: `/user/bank-account/verify-with-otp`,
      requestData: payload,
      responseSchema: {},
    });
  },

  sendForApproval: ({
    bankAccountId,
    reason,
  }: {
    bankAccountId: number | string;
    reason?: string;
  }) => {
    return postApi<{ status: string }, { reason?: string }>({
      url: `/user/bank-account/${bankAccountId}/process-approval`,
      responseSchema: {},
      requestData: {
        reason,
      },
    });
  },

  getBankAccountRequests: (queryParams: GetBankAccountRequestsQueryParams) => {
    const urlParamsV2 = prepareUrlParams(queryParams, { arrayFormat: 'bracket' });
    const url = '/requests/bank-accounts' + urlParamsV2;
    return getApi<GetBankAccountRequestsResponse>({
      url,
      responseSchema: {},
    });
  },

  getBankAccountRequestDetails: ({ bank_account_id }: { bank_account_id: number | string }) => {
    const url = `/request/bank-account/${bank_account_id}`;
    return getApi<BankAccountRequestDetails>({
      url,
      responseSchema: {},
    });
  },

  bankAccountRequestApprove: (payload: ApproveRejectPayload) => {
    return postApi<ApproveRejectResponse, ApproveRejectPayload>({
      url: '/bank-account/approve',
      responseSchema: {},
      requestData: payload,
    });
  },

  bankAccountRequestReject: (payload: ApproveRejectPayload) => {
    return postApi<ApproveRejectResponse, ApproveRejectPayload>({
      url: '/bank-account/reject',
      responseSchema: {},
      requestData: payload,
    });
  },
};

export default employeeBankAccount;
